/**
 * Reusable Toggle Visibility Script
 * Handles show/hide functionality with accessibility support
 */

class ToggleVisibility {
	constructor() {
		this.toggleButtons = document.querySelectorAll('.js-toggle-visibility');
		this.init();
	}

	init() {
		if (!this.toggleButtons.length) return;

		this.toggleButtons.forEach((button) => {
			this.setupButton(button);
			this.addEventListeners(button);
		});

		// Handle escape key to close any open toggles
		document.addEventListener('keydown', this.handleEscapeKey.bind(this));

		// Handle clicks outside for buttons with close-on-outside-click attribute
		document.addEventListener('click', this.handleOutsideClick.bind(this));
	}

	setupButton(button) {
		const controlsId = button.getAttribute('aria-controls');
		const targetElement = controlsId ? document.getElementById(controlsId) : null;

		if (!targetElement) {
			console.warn('Toggle button missing valid aria-controls target:', button);
			return;
		}

		// Set initial ARIA attributes if not already set
		if (!button.hasAttribute('aria-expanded')) {
			const isVisible = !targetElement.hidden && getComputedStyle(targetElement).display !== 'none';
			button.setAttribute('aria-expanded', isVisible.toString());
		}

		// Ensure target has proper attributes
		if (!targetElement.hasAttribute('aria-hidden')) {
			const isExpanded = button.getAttribute('aria-expanded') === 'true';
			targetElement.setAttribute('aria-hidden', (!isExpanded).toString());
		}

		// Store reference for easy access
		button._toggleTarget = targetElement;
	}

	addEventListeners(button) {
		// Click handler
		button.addEventListener('click', (e) => {
			e.preventDefault();
			this.toggle(button);
		});

		// Keyboard handler
		button.addEventListener('keydown', (e) => {
			// Space or Enter key
			if (e.key === ' ' || e.key === 'Enter') {
				e.preventDefault();
				this.toggle(button);
			}
		});
	}

	toggle(button) {
		const targetElement = button._toggleTarget;
		if (!targetElement) return;

		const isExpanded = button.getAttribute('aria-expanded') === 'true';
		const newState = !isExpanded;

		// Update ARIA attributes
		button.setAttribute('aria-expanded', newState.toString());
		targetElement.setAttribute('aria-hidden', (!newState).toString());

		// Update aria-label for better screen reader feedback
		const label = button.getAttribute('aria-label');
		if (label) {
			if (newState) {
				// Show state - change to "Hide"
				button.setAttribute('aria-label', label.replace(/show|open/i, 'Hide'));
			} else {
				// Hide state - change to "Show"
				button.setAttribute('aria-label', label.replace(/hide|close/i, 'Show'));
			}
		}

		// Toggle visibility with CSS class or direct property
		if (newState) {
			this.showElement(targetElement);
			this.handleFocusOnShow(targetElement);
		} else {
			this.hideElement(targetElement);
			this.handleFocusOnHide(button);
		}

		// Dispatch custom event for other scripts to listen to
		button.dispatchEvent(
			new CustomEvent('toggleVisibility', {
				detail: {
					isVisible: newState,
					target: targetElement,
					button: button,
				},
			}),
		);
	}

	showElement(element) {
		// Remove hidden attribute
		element.hidden = false;

		// Add show class (for CSS animations)
		element.classList.add('is-visible');
		element.classList.remove('is-hidden');
	}

	hideElement(element) {
		// Add hidden class first (for CSS animations)
		element.classList.add('is-hidden');
		element.classList.remove('is-visible');

		// Use a small delay for animations, then fully hide
		setTimeout(() => {
			element.hidden = true;
		}, 150); // Adjust timing to match your CSS transitions
	}

	handleFocusOnShow(targetElement) {
		// Focus first focusable element within the target
		const focusableElements = targetElement.querySelectorAll(
			'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
		);

		if (focusableElements.length > 0) {
			focusableElements[0].focus();
		}
	}

	handleFocusOnHide(button) {
		// Return focus to the toggle button
		button.focus();
	}

	handleEscapeKey(e) {
		if (e.key === 'Escape') {
			// Close all open toggles
			this.toggleButtons.forEach((button) => {
				if (button.getAttribute('aria-expanded') === 'true') {
					this.toggle(button);
				}
			});
		}
	}

	handleOutsideClick(e) {
		// Only handle buttons with the close-on-outside-click attribute
		const buttonsWithOutsideClose = Array.from(this.toggleButtons).filter((button) =>
			button.hasAttribute('data-close-on-outside-click'),
		);

		buttonsWithOutsideClose.forEach((button) => {
			// Skip if button is not expanded
			if (button.getAttribute('aria-expanded') !== 'true') return;

			const targetElement = button._toggleTarget;
			if (!targetElement) return;

			// Check if click was outside both the button and its controlled element
			const clickedOnButton = button.contains(e.target);
			const clickedOnTarget = targetElement.contains(e.target);

			if (!clickedOnButton && !clickedOnTarget) {
				this.toggle(button);
			}
		});
	}

	// Public methods for external control
	show(buttonOrId) {
		const button =
			typeof buttonOrId === 'string'
				? document.querySelector(`[aria-controls="${buttonOrId}"]`)
				: buttonOrId;

		if (button && button.getAttribute('aria-expanded') === 'false') {
			this.toggle(button);
		}
	}

	hide(buttonOrId) {
		const button =
			typeof buttonOrId === 'string'
				? document.querySelector(`[aria-controls="${buttonOrId}"]`)
				: buttonOrId;

		if (button && button.getAttribute('aria-expanded') === 'true') {
			this.toggle(button);
		}
	}

	// Hide all open toggles
	hideAll() {
		this.toggleButtons.forEach((button) => {
			if (button.getAttribute('aria-expanded') === 'true') {
				this.toggle(button);
			}
		});
	}
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
	window.toggleVisibility = new ToggleVisibility();
});

// Export for module systems
if (typeof module !== 'undefined' && module.exports) {
	module.exports = ToggleVisibility;
}
